<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Tag: {{ tag.name }} Details</span>
      <a-button size="large" class="a-header--button slide-down-enter-active" @click="goBack">
        <a-icon type="arrow-left" />
        Back to Tags
      </a-button>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>
    <div class="a-body flex-grow-1 overflow-y-auto p-20-px" v-else>
      <transition name="slide-up">
        <div>

          <!-- Tag Edit Form Section -->
          <div class="tag-edit-section mb-4">
          <a-card title="Edit Tag" class="mb-4">
            <a-form :form="tagForm" @submit="onSubmit">
              <a-row :gutter="20">
                <a-col :span="12">
                  <a-form-item label="Tag Name" class="name">
                    <a-input
                      v-decorator="[
                        'name',
                        {
                          rules: [
                            {
                              required: true,
                              message: 'Please input Tag Name!'
                            }
                          ]
                        }
                      ]"
                      placeholder="Tag Name"
                    >
                    </a-input>
                  </a-form-item>
                </a-col>
              </a-row>
              <a-row type="flex" justify="end" :gutter="16">
                <a-col>
                  <a-button @click="goBack">Cancel</a-button>
                </a-col>
                <a-col>
                  <a-button type="primary" html-type="submit" :loading="isSubmitting">
                    Update Tag
                  </a-button>
                </a-col>
              </a-row>
            </a-form>
          </a-card>
        </div>

        <!-- URLs Section -->
        <div class="urls-section">
          <a-card :title="`URLs with Tag: ${tag.name}`">

<!-- Action buttons -->
<div class="mb-3">
                <a-space>
                  
                  <!-- Delete Selected URLs -->
                  <a-button
                    size="default"
                    :disabled="!isHasSelected"
                    class="danger-btn"
                    @click="onBatchDelete">
                    <a-icon type="delete" />
                    Delete Selected ({{ selectedRowKeys.length }})
                  </a-button>

                  <!-- Batch Select URLs -->
                  <a-button
                    type="primary"
                    size="default"
                    @click="showBatchSelectUrlsModal">
                    <a-icon type="check-square" />
                    Batch Select URLs
                  </a-button>

                  <!-- Add URL Relations -->
                  <a-button
                    type="default"
                    size="default"
                    @click="showAddRelationsModal">
                    <a-icon type="plus" />
                    Add URL Relations
                  </a-button>
                </a-space>

                <!-- Batch Select URLs Modal -->
                <a-modal
                  :closable="false"
                  title="Batch Select URL Relations"
                  width="45vw"
                  :visible="isShowBatchSelectUrlsModal"
                  @cancel="closeBatchSelectUrlsModal"
                  @ok="handleSubmitBatchSelectUrls"
                >
                  <a-textarea
                    :placeholder="`Paste URLs to select (one URL per line):\neg. http://www.example.com/index.html\nhttps://www.example.com/about.html\nhttp://www.example.com/contact.html`"
                    :autosize="{ minRows: 12, maxRows: 14 }"
                    :value="batchSelectUrls"
                    @change="handleChangeBatchSelectUrls"
                  />
                </a-modal>

                <!-- Add URL Relations Modal -->
                <a-modal
                  :closable="false"
                  :title="`Add URL Relations ${selectedUrlsForAdd.length > 0 ? '(' + selectedUrlsForAdd.length + ' selected)' : ''}`"
                  width="80vw"
                  :visible="isShowAddRelationsModal"
                  @cancel="closeAddRelationsModal"
                  @ok="handleSubmitAddRelations"
                  :confirmLoading="isLoadingUrls"
                  :okButtonProps="{ props: { disabled: selectedUrlsForAdd.length === 0 } }"
                  :okText="selectedUrlsForAdd.length > 0 ? `Add ${selectedUrlsForAdd.length} URL(s)` : 'Add URLs'"
                >
                  <!-- Add URL Relations Modal Content -->
                  <div v-if="!isLoadingUrls">
                    <div class="mb-3">
                      <span class="text-muted">
                        Select URLs to add to the "{{ tag.name }}" tag. Only URLs not already associated with this tag are shown.
                      </span>
                    </div>

                    <!-- Search input for Add Relations -->
                    <div class="mb-3">
                      <div class="full-w a-input-group" :class="{ active: !!addRelationsSearch }">
                        <a-button size="large">
                          <a-icon type="search" />
                        </a-button>
                        <a-input 
                          class="custom-prefix" 
                          placeholder="Search available URLs" 
                          v-model="addRelationsSearch"
                          @input="onAddRelationsSearch"
                        />
                      </div>
                    </div>

                    <!-- Add URL Relations Table -->
                    <PaginationTable
                      :pagination="addRelationsPagin"
                      :columns="addRelationsColumns"
                      :dataSource="filteredAvailableUrls"
                      :hasSelectRow="true"
                      @changeSelectedRow="onAddRelationsSelectionChange"
                      :scroll="{ y: 400 }"
                    >
                      <template slot="url" slot-scope="{ record }">
                        <a :href="record.url" target="_blank" class="url-link">{{ record.url }}</a>
                      </template>
                      <template slot="urlConfigName" slot-scope="{ record }">
                        {{ record.config_name || 'N/A' }}
                      </template>
                    </PaginationTable>
                    <div class="a-footer">
                      <PaginationBar storageKey="addRelationsPageSize" :pageSize.sync="addRelationsPagin.pageSize" :total="addRelationsPagin.total" @change="addRelationsPaginChange" />
                    </div>
                  </div>

                  <!-- Add URL Relations Loading -->
                  <div v-else class="text-center py-4">
                    <Spinner />
                    <div class="mt-2">Loading available URLs...</div>
                  </div>
                </a-modal>
              </div>

            <div v-if="tagUrls.length === 0" class="text-center py-5">
              <a-empty description="No URLs found for this tag" />
            </div>
            <div v-else>

              <!-- Search input -->
              <div class="mb-3">
                <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
                  <a-button size="large">
                    <a-icon type="search" />
                  </a-button>
                  <a-input class="custom-prefix" placeholder="Search URLs" @input="onSearch" />
                </div>
              </div>

              <!-- URLs Table -->
              <div v-resize="elSizeChange">
              <PaginationTable
                :pagination="pagin"
                :columns="urlColumns"
                :dataSource="filteredUrls"
                :tableSize="tableSize"
                :hasSelectRow="true"
                @changeSelectedRow="onSelectChange">
                <template slot="url" slot-scope="{ record }">
                  <a :href="record.url" target="_blank" class="url-link">{{ record.url }}</a>
                </template>
                <template slot="actions" slot-scope="{ record }">
                  <a-button size="small" type="danger" @click="showDeleteConfirm(record)">
                    <a-icon type="delete" />
                  </a-button>
                </template>
              </PaginationTable>
              </div>
            </div>
          </a-card>
        </div>
        </div>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import { PAGE_OPTIONS } from '@constants/pageOptions';
  import TagsService from '../services/tags.service';
  import WebpageService from '../../Webpages/services/webpage.service';

  export default {
    name: 'TagDetail',
      data() {
      let pageSize = Number(window.localStorage.getItem("pageSize"));
      if(!pageSize)
       pageSize = 5;

      let addRelationsPageSize = Number(window.localStorage.getItem("addRelationsPageSize"));
      if(!addRelationsPageSize)
        addRelationsPageSize = 10;

      return {
        PAGE_OPTIONS,
        tag: {},
        tagUrls: [],
        filterData: [],
        isLoading: true,
        isSubmitting: false,
        filterParams: {
          search: ''
        },
        // URLs Table
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0
        },

        // Add URL Relations Table
        addRelationsPagin: {
          current: 1,
          pageSize: addRelationsPageSize,
          total: 0
        },

        // Add URL Relations Table Size
        addRelationsTableSize: {
          height: '',
          width: ''
        },

        // URLs Table Size
        tableSize: {
          height: '',
          width: ''
        },

        selectedRowKeys: [],
        isShowBatchSelectUrlsModal: false,
        batchSelectUrls: '',
        isShowAddRelationsModal: false,
        availableUrls: [],
        selectedUrlsForAdd: [],
        isLoadingUrls: false,
        addRelationsSearch: '',
        urlColumns: [
          {
            title: '#',
            dataIndex: 'index',
            sorter: (a, b) => a.index - b.index,
            width: 60,
            align: 'center'
          },
          {
            title: 'URL Config Name',
            dataIndex: 'urlConfigName',
            sorter: (a, b) => (a.urlConfigName || '').localeCompare(b.urlConfigName || ''),
            ellipsis: true,
            width: 200
          },
          {
            title: 'URL',
            dataIndex: 'url',
            slot: 'url',
            sorter: (a, b) => (a.url || '').localeCompare(b.url || ''),
            ellipsis: true,
            width: 300
          },
          {
            title: 'Actions',
            dataIndex: '',
            slot: 'actions',
            width: 120,
            align: 'center'
          }
        ],
        addRelationsColumns: [
          {
            title: '#',
            dataIndex: 'index',
            sorter: (a, b) => a.index - b.index,
            width: 30,
            align: 'center'
          },
          {
            title: 'URL Config Name',
            dataIndex: 'urlConfigName',
            slot: 'urlConfigName',
            sorter: (a, b) => (a.urlConfigName || '').localeCompare(b.urlConfigName || ''),
            ellipsis: true,
            width: 100
          },
          {
            title: 'URL',
            dataIndex: 'url',
            slot: 'url',
            sorter: (a, b) => (a.url || '').localeCompare(b.url || ''),
            ellipsis: true,
            width: 300
          },
        ]
      };
    },
    computed: {
      isHasSelected() {
        return this.selectedRowKeys.length > 0;
      },
      filteredUrls() {
        const searchTerm = this.filterParams.search.trim().toLowerCase();
        if (!searchTerm) {
          this.filterData = [...this.tagUrls];
        } else {
          this.filterData = this.tagUrls.filter(item => {
            const urlMatch = (item.url || '').toLowerCase().includes(searchTerm);
            const configNameMatch = (item.urlConfigName || '').toLowerCase().includes(searchTerm);
            return urlMatch || configNameMatch;
          });
        }
        // Update pagination total
        this.pagin.total = this.filterData.length;
        
        // Mark selected items with isChecked property for the PaginationTable component
        return this.filterData.map(item => ({
          ...item,
          isChecked: this.selectedRowKeys.includes(item.id)
        }));
      },
      filteredAvailableUrls() {
        const searchTerm = this.addRelationsSearch.trim().toLowerCase();
        let filtered = this.availableUrls;
        
        if (searchTerm) {
          filtered = this.availableUrls.filter(item => {
            const urlMatch = (item.url || '').toLowerCase().includes(searchTerm);
            const configNameMatch = (item.config_name || '').toLowerCase().includes(searchTerm);
            return urlMatch || configNameMatch;
          });
        }

        // Update pagination total
        this.addRelationsPagin.total = filtered.length;
        
        // Mark selected items with isChecked property for the PaginationTable component
        return filtered.map(item => ({
          ...item,
          isChecked: this.selectedUrlsForAdd.includes(item.id)
        }));
      }
    },
    mounted() {
      this.$nextTick(() => {
        this.tagForm = this.$form.createForm(this);
      });
      this.getTagAndUrls();
    },
    methods: {
      goBack() {
        this.$router.push('/tags/list');
      },
      onDeleteRelation(record) {
        TagsService.removeUrlFromTag(this.tag.id, record.id).then(() => {
          this.$showSuccessMessage('URL relation removed from tag');
          // Refresh the data
          this.getTagAndUrls();
        }).catch(() => {
          this.$showErrorMessage('Failed to remove URL relation');
        });
      },

      showDeleteConfirm(record) {
        this.$confirm({
          title: 'Are you sure you want to remove this URL relation?',
          content: `This will remove the relation between "${this.tag.name}" tag and "${record.urlConfigName || record.url}"`,
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: () => {
            this.onDeleteRelation(record);
          }
        });
      },
      getStatusColor(status) {
        const statusColors = {
          'Up': 'green',
          'Down': 'red',
          'Warning': 'orange',
          'Unknown': 'gray'
        };
        return statusColors[status] || 'gray';
      },
      formatDisplayName(urlConfigName) {
        if (!urlConfigName || !urlConfigName.trim()) return 'N/A';
        // Convert ALL CAPS to Title Case
        return urlConfigName.trim().toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
      },
      onSubmit(event) {
        event.preventDefault();
        this.tagForm.validateFields((err, values) => {
          if (!err) {
            this.isSubmitting = true;
            TagsService.update(this.tag.id, { tagName: values.name }).then(() => {
              this.$showSuccessMessage('Tag updated successfully');
              this.tag = { ...this.tag, ...values };
            }).catch(() => {
              this.$showErrorMessage('Failed to update tag');
            }).finally(() => {
              this.isSubmitting = false;
            });
          }
        });
      },

      /**
       * Handle pagination change from PaginationBar
       * ---> Pagination UI interaction > [paginChange] > Update pagination state
       */
      paginChange(pagin) {
        this.pagin = { ...pagin };
      },

      addRelationsPaginChange(pagin) {
        this.addRelationsPagin = { ...pagin };
      },

      /**
       * Handle table size change
       * ---> Resize directive > [elSizeChange] > Update table size
       */
      elSizeChange(size) {
        this.tableSize = size;
      },

      /**
       * Handle row selection change
       * ---> PaginationTable selection > [onSelectChange] > Update selected row keys
       */
      onSelectChange(selectedRowKeys) {
        this.selectedRowKeys = selectedRowKeys;
      },

      /**
       * Handle batch delete of selected URL relations
       * ---> Batch delete button > [onBatchDelete] > Remove multiple URL relations
       */
      onBatchDelete() {
        const selectedCount = this.selectedRowKeys.length;
        this.$confirm({
          title: `Are you sure you want to remove ${selectedCount} URL relation(s)?`,
          content: `This will remove the relation between "${this.tag.name}" tag and ${selectedCount} URL(s)`,
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: async () => {
            try {
              // Delete each selected relation
              for (const urlId of this.selectedRowKeys) {
                await TagsService.removeUrlFromTag(this.tag.id, urlId);
              }

              this.$showSuccessMessage(`${selectedCount} URL relation(s) removed successfully`);
              this.selectedRowKeys = []; // Clear selection
              this.getTagAndUrls(); // Refresh data
            } catch (error) {
              this.$showErrorMessage('Failed to remove some URL relations');
            }
          }
        });
      },
      showBatchSelectUrlsModal() {
        // Pre-populate with currently selected URLs
        const preselectedUrls = this.filteredUrls
          .filter((item) => this.selectedRowKeys.includes(item.id))
          .map((item) => item.url);
        this.batchSelectUrls = preselectedUrls.join('\n');
        this.isShowBatchSelectUrlsModal = true;
      },
      closeBatchSelectUrlsModal() {
        this.isShowBatchSelectUrlsModal = false;
        this.batchSelectUrls = '';
      },
      handleChangeBatchSelectUrls(e) {
        this.batchSelectUrls = e.target.value;
      },
      handleSubmitBatchSelectUrls() {
        try {
          // Parse URLs from textarea (one per line, trimmed)
          const selectedUrls = this.batchSelectUrls
            .split('\n')
            .map((url) => url.trim())
            .filter((url) => url.length > 0);

          // Find matching URL IDs from the current filtered data
          const selectedUrlIds = selectedUrls.map((url) => {
            const record = this.filteredUrls.find((item) => item.url === url);
            if (!record) {
              throw new Error(`URL '${url}' does not exist in this tag's URL list`);
            }
            return record.id;
          });

          // Update selection: uncheck removed URLs, check added URLs
          this.selectedRowKeys.forEach((rowKey) => {
            if (!selectedUrlIds.includes(rowKey)) {
              // URL was deselected
              this.selectedRowKeys = this.selectedRowKeys.filter((key) => key !== rowKey);
            }
          });

          // Add newly selected URLs
          selectedUrlIds.forEach((urlId) => {
            if (!this.selectedRowKeys.includes(urlId)) {
              this.selectedRowKeys.push(urlId);
            }
          });

          this.$showSuccessMessage(`Batch selected ${selectedUrlIds.length} URL(s) successfully`);
          this.closeBatchSelectUrlsModal();
        } catch (err) {
          this.$showErrorMessage(err.message);
        }
      },
      async showAddRelationsModal() {
        this.isShowAddRelationsModal = true;
        this.selectedUrlsForAdd = [];
        await this.loadAvailableUrls();
      },
      closeAddRelationsModal() {
        this.isShowAddRelationsModal = false;
        this.availableUrls = [];
        this.selectedUrlsForAdd = [];
        this.addRelationsSearch = '';
      },
      async loadAvailableUrls() {
        try {
          this.isLoadingUrls = true;

          // Get all URLs
          // I get it in batches of 1000 cause default is 100 and I dont want to spam call the API
          // 1000 means at max customer (MINDEF) I only calling this 5 times
          const allUrls = await WebpageService.getList({ itemsPerPage: 1000 });

          // Get URLs already associated with this tag
          const tagUrls = this.tagUrls.map(url => url.id);

          // Filter out URLs already associated with this tag
          this.availableUrls = allUrls.filter(url => !tagUrls.includes(url.id));

          // Transform the data to match the columns
          this.availableUrls = this.availableUrls.map((item, index) => ({
            ...item,
            index: index + 1
          }));

          // Update pagination total
          this.addRelationsPagin.total = this.availableUrls.length;
          this.addRelationsPagin.current = 1;
        } catch (error) {
          this.$showErrorMessage('Failed to load available URLs');
        } finally {
          this.isLoadingUrls = false;
        }
      },
      onAddRelationsSelectionChange(selectedRowKeys) {
        this.selectedUrlsForAdd = selectedRowKeys;
      },
      async handleSubmitAddRelations() {
        if (this.selectedUrlsForAdd.length === 0) {
          this.$showErrorMessage('Please select at least one URL to add');
          return;
        }

        try {
          // Add relations for all selected URLs using batch API
          await TagsService.addUrlsToTagBatch(this.tag.id, this.selectedUrlsForAdd);
          this.$showSuccessMessage(`Successfully added ${this.selectedUrlsForAdd.length} URL relation(s) to tag "${this.tag.name}"`);
          this.closeAddRelationsModal();

          // Refresh the tag URLs list
          this.getTagAndUrls();
        } catch (error) {
          this.$showErrorMessage('Failed to add some URL relations');
        }
      },
      onSearch: debounce(function (event) {
        const searchTerm = (event.target.value && String(event.target.value).trim().toLowerCase()) || '';
        this.filterParams.search = searchTerm;
        // Reset to first page when search changes
        this.pagin.current = 1;
        // Trigger computed property update
        this.$forceUpdate();
      }, 800),
      onAddRelationsSearch: debounce(function (event) {
        // The v-model handles the binding, just trigger an update
        this.$forceUpdate();
      }, 500),
      async getTagAndUrls() {
        try {
          const tagId = this.$route.params.id;
          const [tagData, urlsData] = await Promise.all([
            TagsService.get(tagId),
            TagsService.getUrlsByTag(tagId)
          ]);

          this.tag = tagData || {};

          // Set form values
          this.$nextTick(() => {
            if (this.tagForm) {
              this.tagForm.setFieldsValue({
                name: this.tag.name || ''
              });
            }
          });

          // Process the URLs data to display in table format
          this.tagUrls = (urlsData || []).map((item, index) => ({
            ...item,
            index: index + 1,
            displayName: this.formatDisplayName(item.urlConfigName)
          }));

          // Update pagination total and initialize filter data
          this.filterData = [...this.tagUrls];
          this.pagin.total = this.tagUrls.length;

        } catch {
          this.$showErrorMessage('Failed to load tag details');
          this.goBack();
        } finally {
          this.isLoading = false;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
.url-link {
  color: #1890ff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.danger-btn {
  border-color: transparent;
  &:hover {
    border-color: transparent;
  }
  &:not([disabled]) {
    color: #f5f5f5;
    background-color: #f5222d;
  }
}
</style>
